import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReconciliationPanel = ({ summary, onStartReconciliation, onViewReport }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  const getVarianceColor = (variance) => {
    if (variance === 0) return 'text-success';
    return Math.abs(variance) > 1000 ? 'text-error' : 'text-warning';
  };

  const reconciliationItems = [
    {
      label: 'Bank Balance',
      amount: summary?.bankBalance,
      icon: 'CreditCard',
      color: 'text-primary'
    },
    {
      label: 'Book Balance',
      amount: summary?.bookBalance,
      icon: 'BookOpen',
      color: 'text-secondary'
    },
    {
      label: 'Outstanding Deposits',
      amount: summary?.outstandingDeposits,
      icon: 'ArrowDownLeft',
      color: 'text-success'
    },
    {
      label: 'Outstanding Withdrawals',
      amount: summary?.outstandingWithdrawals,
      icon: 'ArrowUpRight',
      color: 'text-error'
    }
  ];

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Reconciliation Summary</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={onViewReport}
          iconName="FileText"
          iconPosition="left"
        >
          View Report
        </Button>
      </div>
      {/* Account Summary */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">Current Account</span>
          <span className="text-sm text-muted-foreground">{summary?.accountName}</span>
        </div>
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-muted-foreground">Last Reconciled</span>
          <span className="text-sm text-foreground">{summary?.lastReconciled}</span>
        </div>
      </div>
      {/* Balance Items */}
      <div className="space-y-4 mb-6">
        {reconciliationItems?.map((item, index) => (
          <div key={index} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className={`w-8 h-8 rounded-lg bg-muted/30 flex items-center justify-center`}>
                <Icon name={item?.icon} size={16} className={item?.color} />
              </div>
              <span className="text-sm font-medium text-foreground">{item?.label}</span>
            </div>
            <span className={`text-sm font-semibold ${item?.color}`}>
              {formatCurrency(item?.amount)}
            </span>
          </div>
        ))}
      </div>
      {/* Variance Alert */}
      <div className={`p-4 rounded-lg border mb-6 ${
        summary?.variance === 0 
          ? 'bg-success/5 border-success/20' :'bg-warning/5 border-warning/20'
      }`}>
        <div className="flex items-center space-x-2 mb-2">
          <Icon 
            name={summary?.variance === 0 ? 'CheckCircle' : 'AlertTriangle'} 
            size={16} 
            className={summary?.variance === 0 ? 'text-success' : 'text-warning'} 
          />
          <span className="text-sm font-medium text-foreground">
            {summary?.variance === 0 ? 'Account Balanced' : 'Variance Detected'}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Variance Amount</span>
          <span className={`text-sm font-semibold ${getVarianceColor(summary?.variance)}`}>
            {formatCurrency(summary?.variance)}
          </span>
        </div>
      </div>
      {/* Reconciliation Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="text-center p-3 bg-muted/20 rounded-lg">
          <p className="text-2xl font-bold text-success">{summary?.reconciledCount}</p>
          <p className="text-xs text-muted-foreground">Reconciled</p>
        </div>
        <div className="text-center p-3 bg-muted/20 rounded-lg">
          <p className="text-2xl font-bold text-warning">{summary?.unreconciledCount}</p>
          <p className="text-xs text-muted-foreground">Unreconciled</p>
        </div>
      </div>
      {/* Cash Flow Projection */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-foreground mb-3">7-Day Cash Flow</h4>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Expected Inflow</span>
            <span className="text-success font-medium">
              {formatCurrency(summary?.expectedInflow)}
            </span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Expected Outflow</span>
            <span className="text-error font-medium">
              {formatCurrency(summary?.expectedOutflow)}
            </span>
          </div>
          <div className="border-t border-border pt-2">
            <div className="flex items-center justify-between text-sm font-semibold">
              <span className="text-foreground">Projected Balance</span>
              <span className={summary?.projectedBalance > 0 ? 'text-success' : 'text-error'}>
                {formatCurrency(summary?.projectedBalance)}
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="space-y-3">
        <Button
          variant="default"
          fullWidth
          onClick={onStartReconciliation}
          iconName="CheckCircle"
          iconPosition="left"
          disabled={summary?.unreconciledCount === 0}
        >
          Start Reconciliation ({summary?.unreconciledCount})
        </Button>
        
        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="Download"
            iconPosition="left"
          >
            Export
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
          >
            Refresh
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReconciliationPanel;